import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy,
  Sparkles,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  Copy,
  Check,
  RotateCcw,
  ShieldCheck,
  Calendar,
  Ticket,
  Users,
  Play
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// ──────────────────────────────────────────────
// GERADOR DE SOM SINTETIZADO (Web Audio API)
// ──────────────────────────────────────────────
class SoundEffects {
  constructor() {
    this.ctx = null;
    this.enabled = true;
  }

  init() {
    if (!this.ctx && typeof window !== "undefined") {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) this.ctx = new AudioCtx();
    }
  }

  playTick(pitch = 900) {
    if (!this.enabled) return;
    this.init();
    if (!this.ctx) return;
    try {
      if (this.ctx.state === "suspended") this.ctx.resume();
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(pitch, this.ctx.currentTime);
      gain.gain.setValueAtTime(0.08, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.04);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.04);
    } catch (_) {}
  }

  playVictory() {
    if (!this.enabled) return;
    this.init();
    if (!this.ctx) return;
    try {
      if (this.ctx.state === "suspended") this.ctx.resume();
      // Fanfarra triunfal em acorde dourado
      const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
      notes.forEach((freq, idx) => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, this.ctx.currentTime + idx * 0.12);
        gain.gain.setValueAtTime(0, this.ctx.currentTime + idx * 0.12);
        gain.gain.linearRampToValueAtTime(0.18, this.ctx.currentTime + idx * 0.12 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + idx * 0.12 + 0.9);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(this.ctx.currentTime + idx * 0.12);
        osc.stop(this.ctx.currentTime + idx * 0.12 + 0.95);
      });
    } catch (_) {}
  }
}

const sfx = new SoundEffects();

// ──────────────────────────────────────────────
// CANVASC CONFETTI DOURADO
// ──────────────────────────────────────────────
function ConfettiCanvas({ active }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    const width = (canvas.width = window.innerWidth);
    const height = (canvas.height = window.innerHeight);

    const colors = [
      "#D4AF37", // Ouro Clássico
      "#F3E5AB", // Ouro Champanhe
      "#AA771C", // Ouro Escuro
      "#FFFFFF", // Brilho Branco
      "#FFE58F"  // Amarelo Dourado
    ];

    const particles = Array.from({ length: 120 }).map(() => ({
      x: width * (0.2 + Math.random() * 0.6),
      y: height * 0.4 + (Math.random() * 50 - 25),
      vx: (Math.random() - 0.5) * 16,
      vy: Math.random() * -14 - 4,
      size: Math.random() * 8 + 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      vRotation: (Math.random() - 0.5) * 12,
      opacity: 1,
      gravity: 0.35,
      friction: 0.985
    }));

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      let anyAlive = false;
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.vx *= p.friction;
        p.vy *= p.friction;
        p.rotation += p.vRotation;

        if (p.y > height * 0.7) {
          p.opacity -= 0.012;
        }

        if (p.opacity > 0) {
          anyAlive = true;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = Math.max(0, p.opacity);
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
          ctx.restore();
        }
      });

      if (anyAlive) {
        animationFrameId = requestAnimationFrame(render);
      }
    };

    render();

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[9999]"
      style={{ width: "100vw", height: "100vh" }}
    />
  );
}

// ──────────────────────────────────────────────
// COMPONENTE PRINCIPAL: SORTEIO ROLETA
// ──────────────────────────────────────────────
export default function SorteioRoleta({
  adminPassword,
  campanhas = [],
  selectedSlug,
  setSelectedSlug
}) {
  const [spinning, setSpinning] = useState(false);
  const [currentDisplayName, setCurrentDisplayName] = useState("---");
  const [winnerData, setWinnerData] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [totalParticipantes, setTotalParticipantes] = useState(null);
  const [nomesPreview, setNomesPreview] = useState([]);

  const containerRef = useRef(null);
  const spinIntervalRef = useRef(null);

  // Carrega contagem de participantes exclusivamente do parceiro selecionado
  const carregarParticipantes = useCallback(async () => {
    if (!selectedSlug) {
      setTotalParticipantes(null);
      setNomesPreview([]);
      return;
    }
    try {
      const headers = { "x-admin-password": adminPassword };
      const url = `${API}/admin/sorteio/participantes?partner_slug=${encodeURIComponent(selectedSlug)}`;
      const res = await axios.get(url, { headers });
      setTotalParticipantes(res.data.total);
      setNomesPreview(res.data.nomes || []);
    } catch (_) {
      setTotalParticipantes(0);
      setNomesPreview([]);
    }
  }, [adminPassword, selectedSlug]);

  useEffect(() => {
    carregarParticipantes();
    setWinnerData(null);
  }, [selectedSlug, carregarParticipantes]);

  // Alterna som
  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    sfx.enabled = next;
    if (next) sfx.playTick(1000);
  };

  // Alterna tela cheia
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  // Máscara de telefone segura nos termos da LGPD
  const maskPhone = (phone) => {
    if (!phone) return "";
    const clean = phone.replace(/\D/g, "");
    if (clean.length < 10) return phone;
    const ddd = clean.slice(0, 2);
    const last4 = clean.slice(-4);
    return `(${ddd}) 9****-${last4}`;
  };

  // Iniciar Sorteio com Roleta de Nomes Exclusivos do Parceiro
  const iniciarSorteio = async () => {
    if (spinning) return;

    if (!selectedSlug) {
      toast.error("Por favor, selecione um parceiro para realizar o sorteio.");
      return;
    }

    if (totalParticipantes === 0) {
      toast.error("Este parceiro não possui nenhum lead cadastrado para sorteio.");
      return;
    }

    const headers = { "x-admin-password": adminPassword };
    setSpinning(true);
    setWinnerData(null);
    setShowConfetti(false);

    try {
      // 1. Dispara a chamada ao backend com o parceiro obrigatório
      const payload = { partner_slug: selectedSlug };
      const resPromise = axios.post(`${API}/admin/sorteio`, payload, { headers });

      // 2. Prepara lista de nomes dos participantes DESTE PARCEIRO para girar na roleta
      // Se houver poucos nomes (ex: 2 ou 3), repete ciclicamente os nomes do próprio parceiro
      let poolNomes = [...nomesPreview];
      if (poolNomes.length === 0) {
        poolNomes = ["Aguardando participantes..."];
      } else while (poolNomes.length < 8) {
        poolNomes = poolNomes.concat(nomesPreview);
      }

      // 3. Mecanismo de Roleta com Física de Desaceleração
      const startTime = Date.now();
      const totalDuration = 5200; // 5.2 segundos de suspense
      let stepInterval = 45; // Inicia em 45ms por nome (alta rotação)
      let nameIndex = 0;

      const runSpinStep = async () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);

        // Desaceleração exponencial suave (ease-out cubic)
        // No início rápido (45ms), no final desacelera até 750ms
        stepInterval = 45 + Math.pow(progress, 3) * 650;

        nameIndex = (nameIndex + 1) % poolNomes.length;
        setCurrentDisplayName(poolNomes[nameIndex]);

        // Efeito sonoro do tick mecânico com pitch descendente suave
        sfx.playTick(950 - progress * 300);

        if (progress < 1) {
          spinIntervalRef.current = setTimeout(runSpinStep, stepInterval);
        } else {
          // Chegamos ao final da desaceleração: revela o vencedor da API
          try {
            const res = await resPromise;
            const data = res.data;

            setCurrentDisplayName(data.ganhador.name);
            setWinnerData(data);
            setSpinning(false);
            setShowConfetti(true);
            sfx.playVictory();
            toast.success(`🎉 Sorteio realizado com sucesso! Ganhador: ${data.ganhador.name}`);
          } catch (err) {
            setSpinning(false);
            const msg = err.response?.data?.detail || "Erro ao realizar sorteio.";
            toast.error(msg);
          }
        }
      };

      // Inicia a rotação
      runSpinStep();
    } catch (err) {
      setSpinning(false);
      const msg = err.response?.data?.detail || "Falha ao iniciar sorteio.";
      toast.error(msg);
    }
  };

  // Copiar resultado para os stories do Instagram
  const copiarParaStories = () => {
    if (!winnerData) return;
    const nomeCampanha = campanhas.find((c) => c.slug === selectedSlug)?.nome_parceiro || "Campanha Oficial";
    const texto = `🏆 GANHADOR(A) DO SORTEIO!\n\n` +
      `👤 Nome: ${winnerData.ganhador.name}\n` +
      `🎟️ Cupom: ${winnerData.cupom}\n` +
      `📱 Telefone: ${maskPhone(winnerData.ganhador.phone)}\n` +
      `📍 Campanha: ${nomeCampanha}\n` +
      `👥 Total de concorrentes: ${winnerData.total_participantes}\n\n` +
      `Parabéns ao ganhador! Entraremos em contato via WhatsApp.\n` +
      `#Sorteio #Patrimonio #JulianoWagner #Expointer`;

    navigator.clipboard.writeText(texto).then(() => {
      setCopied(true);
      toast.success("Texto copiado! Pronto para colar nos seus Stories ou WhatsApp.");
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const campanhaAtual = campanhas.find((c) => c.slug === selectedSlug);

  return (
    <div
      ref={containerRef}
      className={`relative transition-all duration-300 ${
        isFullscreen
          ? "fixed inset-0 z-50 bg-[#090807] text-foreground p-6 sm:p-12 flex flex-col justify-between overflow-y-auto"
          : "max-w-3xl mx-auto"
      }`}
    >
      <ConfettiCanvas active={showConfetti} />

      {/* Top Header Controls */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 px-3 py-1 rounded-full text-xs font-medium text-primary">
            <Sparkles size={13} className="text-primary animate-pulse" />
            <span>Sorteio Oficial com Roleta</span>
          </div>
          {totalParticipantes !== null && (
            <span className="text-xs text-muted-foreground font-mono bg-white/5 border border-border px-2.5 py-1 rounded-full flex items-center gap-1">
              <Users size={12} className="text-primary" /> {totalParticipantes} inscritos
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Som Mute/Unmute */}
          <button
            onClick={toggleSound}
            className={`p-2 rounded-full border transition-colors cursor-pointer ${
              soundOn
                ? "border-primary/40 bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
            title={soundOn ? "Desativar som" : "Ativar som"}
          >
            {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>

          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors cursor-pointer"
            title={isFullscreen ? "Sair da tela cheia" : "Modo Gravação / Instagram Stories (Tela Cheia)"}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>

      {/* Seletor de Campanha Obrigatório */}
      {!isFullscreen && (
        <div className="mb-6 bg-card-gradient border border-border/60 rounded-2xl p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex-1">
              <label className="block text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">
                Selecione o Parceiro Oficial <span className="text-primary">*</span>
              </label>
              <select
                id="select-campanha-sorteio"
                value={selectedSlug}
                onChange={(e) => {
                  setSelectedSlug(e.target.value);
                  setWinnerData(null);
                }}
                disabled={spinning}
                className="w-full sm:w-96 px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:border-primary/70 cursor-pointer disabled:opacity-50"
              >
                <option value="" disabled>-- Selecione o Parceiro para o Sorteio --</option>
                {campanhas.map((c) => (
                  <option key={c.id} value={c.slug}>
                    {c.nome_parceiro} — Prêmio: {c.premio}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-muted-foreground block mt-1">
                O sorteio incluirá exclusivamente os participantes cadastrados para este parceiro.
              </span>
            </div>

            {campanhaAtual && (
              <div className="text-right sm:border-l sm:border-border/60 sm:pl-4 shrink-0">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground block">Prêmio do Parceiro</span>
                <span className="font-display text-sm font-medium text-gold-gradient block">{campanhaAtual.premio}</span>
                <span className="text-[11px] font-mono text-emerald-400 font-semibold">
                  {totalParticipantes !== null ? `${totalParticipantes} concorrentes` : "Carregando..."}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────── */}
      {/* DISPLAY DA ROLETA DIGITAL (ESTILO INSTAGRAMÁVEL) */}
      {/* ────────────────────────────────────────────── */}
      <div className="bg-card-gradient border-2 border-primary/40 rounded-3xl p-6 sm:p-10 shadow-gold relative overflow-hidden text-center">
        {/* Luzes decorativas de fundo */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-48 bg-primary/15 rounded-full blur-3xl pointer-events-none -z-10" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl pointer-events-none -z-10" />

        {/* Cabeçalho da Roleta */}
        <div className="mb-6">
          <span className="text-[11px] uppercase tracking-[0.25em] font-mono text-primary block mb-1">
            {campanhaAtual
              ? `Sorteio Oficial · Parceiro ${campanhaAtual.nome_parceiro}`
              : "Selecione um Parceiro para Iniciar"}
          </span>
          <h2 className="font-display text-2xl sm:text-4xl font-light">
            {campanhaAtual ? (
              <>Sorteio de <span className="italic text-gold-gradient font-normal">{campanhaAtual.premio}</span></>
            ) : (
              <>Roleta de Sorteio por <span className="italic text-gold-gradient font-normal">Parceiro</span></>
            )}
          </h2>
        </div>

        {/* JANELA DA ROLETA / TICKER DE NOMES */}
        <div className="relative my-8 max-w-xl mx-auto">
          {/* Marcador Central de Parada (Estilo Roleta de Cassino) */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 text-primary drop-shadow-[0_2px_8px_rgba(212,175,55,0.8)]">
            <div className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[14px] border-t-primary animate-bounce" />
          </div>

          <div
            className={`relative h-28 sm:h-36 rounded-2xl border-2 flex items-center justify-center px-6 transition-all duration-300 overflow-hidden ${
              spinning
                ? "border-primary bg-primary/10 shadow-[0_0_40px_rgba(212,175,55,0.35)] ring-2 ring-primary/50"
                : winnerData
                ? "border-emerald-500/60 bg-emerald-500/5 shadow-[0_0_35px_rgba(16,185,129,0.25)]"
                : "border-border bg-background/60"
            }`}
          >
            {/* Linhas de reflexo estilo vidro/neon */}
            <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />

            <AnimatePresence mode="popLayout">
              {spinning ? (
                <motion.div
                  key={currentDisplayName}
                  initial={{ opacity: 0.6, y: -25, filter: "blur(2px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0.4, y: 25, filter: "blur(2px)" }}
                  transition={{ duration: 0.08, ease: "linear" }}
                  className="font-display text-2xl sm:text-4xl font-semibold text-gold-gradient truncate max-w-full px-4"
                >
                  {currentDisplayName}
                </motion.div>
              ) : winnerData ? (
                <motion.div
                  key="winner-display"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", bounce: 0.45 }}
                  className="text-center"
                >
                  <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono block mb-1">
                    Vencedor(a) Sorteado(a)
                  </span>
                  <div className="font-display text-2xl sm:text-4xl font-bold text-gold-gradient truncate max-w-full px-4">
                    {winnerData.ganhador.name}
                  </div>
                </motion.div>
              ) : (
                <div className="text-center text-muted-foreground">
                  <Trophy size={28} className="mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm font-medium">Clique no botão abaixo para girar a roleta</p>
                </div>
              )}
            </AnimatePresence>
          </div>

          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-20 text-primary drop-shadow-[0_-2px_8px_rgba(212,175,55,0.8)]">
            <div className="w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[14px] border-b-primary animate-bounce" />
          </div>
        </div>

        {/* ────────────────────────────────────────────── */}
        {/* CARD DO GANHADOR (INSTAGRAMÁVEL / GRAVAÇÃO) */}
        {/* ────────────────────────────────────────────── */}
        {winnerData && !spinning && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-background/90 border border-primary/40 rounded-2xl p-6 max-w-md mx-auto mb-8 shadow-2xl relative overflow-hidden text-center backdrop-blur-md"
          >
            <div className="w-14 h-14 rounded-full bg-primary/15 ring-2 ring-primary/40 text-primary flex items-center justify-center mx-auto mb-3">
              <Trophy size={28} />
            </div>

            <div className="inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-400 text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full mb-2">
              <ShieldCheck size={12} /> Sorteio Validado Criptograficamente
            </div>

            {/* Destaque do Parceiro e Prêmio */}
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-3 mb-3">
              <span className="text-[10px] uppercase tracking-widest text-primary font-mono block mb-0.5">
                Parceiro Oficial: {winnerData.parceiro || campanhaAtual?.nome_parceiro}
              </span>
              <span className="font-display text-base font-semibold text-gold-gradient">
                🎁 {winnerData.premio || campanhaAtual?.premio}
              </span>
            </div>

            <h3 className="font-display text-2xl sm:text-3xl font-light text-foreground mb-1">
              {winnerData.ganhador.name}
            </h3>

            {/* Telefone com máscara de privacidade LGPD para Stories */}
            <p className="font-mono text-sm text-primary font-medium mb-4">
              📱 {maskPhone(winnerData.ganhador.phone)}
            </p>

            {/* Informações em caixas douradas */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              <div className="bg-white/5 border border-border/80 rounded-xl p-2.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground block flex items-center justify-center gap-1 mb-0.5">
                  <Ticket size={11} className="text-primary" /> Cupom
                </span>
                <span className="font-mono font-bold text-base text-gold-gradient">{winnerData.cupom}</span>
              </div>

              <div className="bg-white/5 border border-border/80 rounded-xl p-2.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground block flex items-center justify-center gap-1 mb-0.5">
                  <Users size={11} className="text-primary" /> Concorrentes do Parceiro
                </span>
                <span className="font-mono font-bold text-base text-foreground">{winnerData.total_participantes}</span>
              </div>
            </div>

            {/* Data e hora */}
            <div className="text-[11px] text-muted-foreground/80 flex items-center justify-center gap-1 mb-4">
              <Calendar size={12} /> {new Date(winnerData.sorteado_em || Date.now()).toLocaleString("pt-BR")}
            </div>

            {/* Botão Copiar para Stories */}
            <button
              onClick={copiarParaStories}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-xs font-medium border border-primary/40 bg-primary/10 hover:bg-primary/20 text-primary transition-all cursor-pointer"
            >
              {copied ? <><Check size={14} /> Copiado para colar nos Stories!</> : <><Copy size={14} /> Copiar Resultado para Stories</>}
            </button>
          </motion.div>
        )}

        {/* Mensagem de alerta se nenhum parceiro estiver selecionado */}
        {!selectedSlug && (
          <div className="max-w-md mx-auto mb-6 p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/20 text-xs text-yellow-400 text-center">
            Selecione o parceiro acima para carregar exclusivamente os leads participantes daquela promoção.
          </div>
        )}

        {/* Mensagem se o parceiro selecionado não tiver participantes */}
        {selectedSlug && totalParticipantes === 0 && (
          <div className="max-w-md mx-auto mb-6 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-400 text-center">
            ⚠️ O parceiro <strong>{campanhaAtual?.nome_parceiro || selectedSlug}</strong> ainda não possui nenhum lead cadastrado.
          </div>
        )}

        {/* BOTÃO PRINCIPAL DE AÇÃO */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center max-w-md mx-auto">
          <button
            id="btn-iniciar-roleta"
            onClick={iniciarSorteio}
            disabled={spinning || !selectedSlug || totalParticipantes === 0}
            className="w-full py-4 rounded-full font-medium text-base transition-all shadow-gold hover:translate-y-[-1px] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
            style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
          >
            {spinning ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-[#0f0d0a] border-t-transparent rounded-full animate-spin" />
                Sorteando entre leads de {campanhaAtual?.nome_parceiro || "parceiro"}...
              </>
            ) : winnerData ? (
              <>
                <RotateCcw size={18} /> Sortear Outro Participante Deste Parceiro
              </>
            ) : !selectedSlug ? (
              <>
                Selecione um Parceiro para Sortear
              </>
            ) : totalParticipantes === 0 ? (
              <>
                Nenhum Participante Deste Parceiro
              </>
            ) : (
              <>
                <Play size={18} fill="#0f0d0a" /> Girar Roleta · Sorteio {campanhaAtual?.nome_parceiro}
              </>
            )}
          </button>
        </div>

        {/* Rodapé informativo */}
        <p className="text-[11px] text-muted-foreground mt-6">
          Sorteio seguro, transparente e com número aleatório auditável. Ideal para lives e gravação de Stories.
        </p>
      </div>
    </div>
  );
}
